/**
 * ContractDetailsStep Component
 * Step 1: Basic contract information, investor, and dates
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  MenuItem,
  Typography,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  InputAdornment,
  IconButton,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { Controller, useFormContext } from 'react-hook-form';
import { addDays, addMonths, addYears, differenceInDays, differenceInMonths, differenceInYears, format } from 'date-fns';
import { useContractFormContext } from '../ContractFormContext';
import { useTenant } from '../../../../contexts/TenantContext';
import ContractInvestorsSection from '../../ContractInvestorsSection';
import StakeholderAutocomplete from '../../../common/StakeholderAutocomplete';
import LabeledSwitch, { InlineGateToggle } from '../../../common/LabeledSwitch';
import ToggleGroup from '../../../common/ToggleGroup';
import RolloverSourcesSection from '../../RolloverSourcesSection';
import ProgramAutocomplete from '../../ProgramAutocomplete';
import { apiGet, extractApiData } from '../../../../utils/apiClient';
import logger from '../../../../utils/logger';

// Format number with space as thousand separator
const formatWithSpaces = (value) => {
  const num = parseFloat(value);
  if (isNaN(num)) return '0.00';
  return num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

// Currency input that shows formatted value when blurred, raw value when focused
function CurrencyInput({ value, onChange, label, helperText, error, disabled, sx, ...props }) {
  const [focused, setFocused] = useState(false);
  const showFormatted = !focused || disabled;
  // When focused, show raw numeric value (no spaces) so type="number" can parse it
  const rawValue = (() => {
    const num = parseFloat(value);
    return isNaN(num) ? '' : String(num);
  })();

  return (
    <TextField
      {...props}
      label={label}
      fullWidth
      value={showFormatted ? formatWithSpaces(value) : rawValue}
      onChange={disabled ? undefined : (e) => {
        const raw = e.target.value.replace(/\s/g, '');
        onChange(raw);
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      type={showFormatted ? 'text' : 'number'}
      inputProps={showFormatted ? {} : { step: 0.01, min: 0 }}
      InputLabelProps={{ shrink: true }}
      error={!!error}
      helperText={error?.message || helperText}
      disabled={disabled}
      sx={sx}
    />
  );
}

// Calculate maturity date based on deposit date and term. Months/Years may be
// fractional (e.g. 2.82 years) — the whole part is added with date-fns and the
// fractional part as that proportion of the next unit's day-span. This is the
// inverse of diffInUnit() below, so term↔maturity round-trips consistently.
const calculateMaturityDate = (depositDate, termUnit, termValue) => {
  const deposit = new Date(depositDate);
  // Defensive: a cleared/invalid deposit date must never flow into date-fns
  // (would throw RangeError on the ISO conversion). Callers already guard on
  // empty depositDate; this is a belt-and-braces backstop.
  if (isNaN(deposit.getTime())) return null;
  const term = parseFloat(termValue);
  if (isNaN(term)) return deposit;
  if (termUnit === 'Days') return addDays(deposit, Math.round(term));

  const addFn = termUnit === 'Months' ? addMonths : termUnit === 'Years' ? addYears : null;
  if (!addFn) return deposit;
  const whole = Math.trunc(term);
  const frac = term - whole;
  const anchor = addFn(deposit, whole);
  if (frac === 0) return anchor;
  const nextAnchor = addFn(deposit, whole + 1);
  const spanDays = differenceInDays(nextAnchor, anchor);
  return addDays(anchor, Math.round(frac * spanDays));
};

// Compute term value from a start/end date pair in the given unit.
// Days is always an integer; Months/Years return up to 2dp for partial spans
// (e.g. 11 months of a year reads as 0.92 years, 3 years + 4 days reads as 3.01).
const diffInUnit = (startIso, endIso, unit) => {
  const s = new Date(startIso);
  const e = new Date(endIso);
  // Defensive: cleared/invalid endpoints must never reach date-fns.
  if (isNaN(s.getTime()) || isNaN(e.getTime())) return null;
  if (unit === 'Days') return differenceInDays(e, s);
  if (unit === 'Months' || unit === 'Years') {
    const wholeFn = unit === 'Months' ? differenceInMonths : differenceInYears;
    const addFn = unit === 'Months' ? addMonths : addYears;
    const whole = wholeFn(e, s);
    const anchor = addFn(s, whole);
    if (anchor.getTime() === e.getTime()) return whole;
    const nextAnchor = addFn(s, whole + 1);
    const spanDays = differenceInDays(nextAnchor, anchor);
    const residualDays = differenceInDays(e, anchor);
    const frac = spanDays > 0 ? residualDays / spanDays : 0;
    return Math.round((whole + frac) * 100) / 100;
  }
  return null;
};

// Native date input (<TextField type="date">) with a one-click clear (✕) button.
// The native date control has no clean whole-field clear — the user otherwise has
// to delete the dd/mm/yyyy segments one at a time. The ✕ shows only when a value
// is present; clicking it empties the field (field.onChange('')) and then runs the
// field-specific onClear (e.g. resetting a "manually set" mode flag). All other
// TextField props (label, helperText, error, inputProps, InputLabelProps) pass through.
function ClearableDateField({ field, onClear, ...textFieldProps }) {
  const hasValue = !!field.value;
  return (
    <TextField
      {...field}
      {...textFieldProps}
      type="date"
      fullWidth
      InputLabelProps={{ shrink: true }}
      InputProps={{
        endAdornment: hasValue ? (
          <InputAdornment position="end" sx={{ mr: 0.5 }}>
            <IconButton
              aria-label="Clear date"
              size="small"
              edge="end"
              onClick={() => {
                field.onChange('');
                onClear?.();
              }}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ) : undefined,
      }}
    />
  );
}

function ContractDetailsStep() {
  const { control, watch, setValue, getFieldState, formState: { errors } } = useFormContext();
  const {
    isEdit,
    isEditable,
    contractId,
    initialData,
    refData,
    appendToRefData,
    contractInvestors,
    setContractInvestors,
    handleAddInvestor,
    handleUpdateInvestor,
    handleRemoveInvestor,
    setProgramRequirement,
    rolledFundsError,
    setRolledFundsError,
    requireLikelihoodToRoll,
  } = useContractFormContext();
  const { getStakeholderLabel } = useTenant();
  const issuerLabel = getStakeholderLabel('stakeholder.issuer.label', 'Issuer');

  // Watch form values for calculations and conditional rendering
  const isMultiInvestor = watch('is_multi_investor');
  const depositDate = watch('deposit_date');
  const investmentTerm = watch('investment_term');
  const investmentTermValue = watch('investment_term_value');
  const maturityDate = watch('maturity_date');
  const investmentCurrencyId = watch('investment_currency_id');
  const settlementCurrencyId = watch('settlement_currency_id');
  const newFundsAmount = watch('new_funds_amount');
  const rolledFundsAmount = watch('rolled_funds_amount');
  const rolledFromContractId = watch('rolled_from_contract_id');
  const rolloverSources = watch('rollover_sources') || [];  // T3-032 many-to-1 source rows
  const issuerId = watch('issuer_id');
  const productId = watch('product_id');
  const capitalAmount = watch('capital_amount');
  const userReference = watch('user_reference');
  const capitalReceivedInSettlementCcy = watch('capital_received_in_settlement_ccy');

  // Get currency code for display
  const selectedCurrency = refData.currencies?.find(c => c.id === investmentCurrencyId);
  const currencyCode = selectedCurrency?.currency_code || 'ZAR';

  // Multi-investor toggle. Turning it OFF on a Draft removes every investor
  // allocation (the backend flips is_multi_investor=false once the last
  // contract_investors row is deleted via sp_contract_remove_investor). Because
  // that is destructive, gate it behind a confirmation when allocations exist.
  // pendingMultiOff holds the RHF onChange callback while the dialog is open.
  const [pendingMultiOff, setPendingMultiOff] = useState(null);

  const handleMultiInvestorToggle = (checked, onChange) => {
    if (checked) {
      // ON: existing behaviour — clear the single-investor selection.
      onChange(true);
      setValue('investor_id', '');
      return;
    }
    if ((contractInvestors || []).length > 0) {
      // OFF with allocations present → confirm before deleting them.
      setPendingMultiOff(() => onChange);
      return;
    }
    // OFF with nothing to lose.
    onChange(false);
  };

  const confirmMultiOff = () => {
    // Empty the in-context array so saveContractInvestors' diff (against
    // originalContractInvestors) deletes every server-side row; the last
    // delete flips is_multi_investor=false in the DB.
    setContractInvestors([]);
    pendingMultiOff?.(false);
    setValue('investor_id', ''); // single-investor selector reappears blank; validation forces a pick
    // Reset the contract-level amounts: they were synced from the (now removed)
    // investor allocations, so leaving them would carry a stale composite total
    // (e.g. 201000 = 100000 + 101000) into a single-investor contract. The user
    // re-enters single-investor New/Rolled funds; capital_amount recomputes from them.
    setValue('capital_amount', '');
    setValue('new_funds_amount', '');
    setValue('rolled_funds_amount', 0);
    // Clear the contract-level rolled-from reference too (it mirrored the first
    // investor's source, which no longer applies).
    setValue('rolled_from_contract_id', null);
    setValue('rolled_from_reference', '');
    setValue('rolled_from_user_reference', '');
    setPendingMultiOff(null);
  };

  // T117 multi-currency settlement: the investor settles capital/interest in a currency
  // different from the contract (accrual) currency. The checkbox reveals a settlement-currency
  // picker; unticking it clears settlement_currency_id (single-currency, default behaviour).
  const [settleDifferent, setSettleDifferent] = useState(false);
  // Initialise the toggle from loaded data (edit mode): on when a distinct settlement ccy is set.
  useEffect(() => {
    if (settlementCurrencyId && settlementCurrencyId !== investmentCurrencyId) {
      setSettleDifferent(true);
    }
  }, [settlementCurrencyId, investmentCurrencyId]);

  // T119: the Initial Capital Rate is a value DERIVED from the settlement→contract currency
  // pair (recommended from the system FX store), and the user may override it. The single
  // source of truth for "the user has taken ownership" is RHF's own dirty tracking
  // (getFieldState(...).isDirty), which lives in form state and therefore SURVIVES this step's
  // unmount/remount on wizard navigation — unlike a ref. The auto-suggestion writes with
  // shouldDirty:false so it never marks the field dirty; only a real keystroke does. That is
  // what lets every currency-pair change re-fetch the correct rate while still respecting a
  // hand-entered agreed rate.
  const settlementCurrencyCode = refData.currencies?.find(c => c.id === settlementCurrencyId)?.currency_code;
  const [fxRecommending, setFxRecommending] = useState(false);

  // T127 (ADDENDUM 2): the currency the capital INPUTS are captured in. When the contract
  // settles in a different currency AND the initial funds arrive in that settlement currency,
  // the user types New Funds / Rolled Funds / per-investor capital in the SETTLEMENT currency;
  // otherwise they are in the contract currency. The fields are labelled with this so there is
  // no ambiguity, and buildPayload converts settlement→contract (× settlement_fx_rate) on save.
  const captureCcy = (settleDifferent && capitalReceivedInSettlementCcy && settlementCurrencyCode)
    ? settlementCurrencyCode
    : currencyCode;
  const capitalInSettlementCcy = settleDifferent && capitalReceivedInSettlementCcy && !!settlementCurrencyCode;
  // Edit mode: a persisted rate is the agreed rate booked for a specific pair. Keep it until
  // the user actually changes a currency. We capture the LOADED pair from initialData (props,
  // not from our own writes) so "pair unchanged since load" can suppress the auto-fetch.
  const initialFxPair = React.useRef(
    isEdit && initialData?.settlement_fx_rate != null && initialData?.settlement_currency_id
      ? `${initialData.settlement_currency_id}->${initialData.investment_currency_id ?? initialData.currency_id}`
      : null
  );
  useEffect(() => {
    if (!settleDifferent || !settlementCurrencyCode || !currencyCode || settlementCurrencyCode === currencyCode) return;

    // The user typed their own rate → it's theirs; never overwrite.
    if (getFieldState('settlement_fx_rate').isDirty) return;

    // Edit mode: keep the loaded agreed rate while the pair is still the one it was booked for.
    const livePair = `${settlementCurrencyId}->${investmentCurrencyId}`;
    if (initialFxPair.current && initialFxPair.current === livePair) return;

    let cancelled = false;
    setFxRecommending(true);
    apiGet(`/exchange-rates/current/${settlementCurrencyCode}/${currencyCode}`)
      .then((resp) => {
        if (cancelled) return;
        const r = parseFloat(resp?.rate?.rate);
        if (Number.isFinite(r) && r > 0) {
          // shouldDirty:false — an auto-suggestion must not lock the field; a later pair change
          // is still free to refresh it. Only a genuine user edit sets isDirty.
          setValue('settlement_fx_rate', String(r), { shouldDirty: false, shouldValidate: true });
        }
      })
      .catch(() => { /* leave as-is; user enters the agreed rate */ })
      .finally(() => { if (!cancelled) setFxRecommending(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settleDifferent, settlementCurrencyCode, currencyCode, settlementCurrencyId, investmentCurrencyId]);

  // T127: soft duplicate-reference warning. Debounced lookup against the backend
  // (fn_contract_reference_in_use via /contracts/check-reference). WARNS only — never
  // blocks Next/Save, because external references can legitimately repeat. In edit mode
  // the current contract is excluded via exclude_id so a contract never flags itself.
  const [refDuplicate, setRefDuplicate] = useState(null); // { count, samples } | null
  useEffect(() => {
    const ref = (userReference || '').trim();
    if (!ref) { setRefDuplicate(null); return undefined; }
    let cancelled = false;
    const handle = setTimeout(() => {
      const params = new URLSearchParams({ ref });
      if (isEdit && contractId) params.set('exclude_id', String(contractId));
      apiGet(`/contracts/check-reference?${params.toString()}`)
        .then((resp) => {
          if (cancelled) return;
          const data = extractApiData(resp);
          setRefDuplicate(data && data.exists ? { count: data.count, samples: data.samples || [] } : null);
        })
        .catch((err) => {
          // Non-fatal: a failed lookup must never block the user. Log and clear.
          logger.warn('External reference duplicate check failed', { message: err.message });
          if (!cancelled) setRefDuplicate(null);
        });
    }, 400);
    return () => { cancelled = true; clearTimeout(handle); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userReference, isEdit, contractId]);

  // T3-032: the rolled-funds cap is owned by RolloverSourcesSection, which publishes a
  // blocking message via its onChange -> setRolledFundsError so the Step-0 validator gates
  // Next. Here we CLEAR that error when the section isn't shown (multi-investor, or no
  // rolled funds), and on unmount, so a stale error can't wedge Step 0.
  useEffect(() => {
    const rolled = parseFloat(rolledFundsAmount) || 0;
    if (isMultiInvestor || rolled <= 0) {
      setRolledFundsError(null);
    }
    return () => setRolledFundsError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMultiInvestor, rolledFundsAmount, setRolledFundsError]);

  // BREAK the link when Rolled Funds settles at 0: clear the source rows + the contract-level
  // mirror so no orphan sources are saved (the section hides at 0, so rows would otherwise
  // linger and the API's SUM(rolled_amount)=rolled_funds_amount check would 400).
  //
  // DEBOUNCED so an in-progress EDIT doesn't wipe the linkages: select-all + delete makes the
  // field transiently empty (parses to 0) before the user types the new value. Clearing on that
  // transient 0 destroyed the sources the user had linked (they didn't come back). We instead
  // wait a short beat; if the value returns to >0 (still typing) the pending clear is cancelled,
  // so only a value that genuinely SETTLES at 0 clears the sources.
  useEffect(() => {
    if (isMultiInvestor) return;
    const rolled = parseFloat(rolledFundsAmount) || 0;
    if (rolled > 0) return; // has a value — nothing to clear, and cancels any pending clear
    const hasSources = (watch('rollover_sources') || []).length > 0 || !!watch('rolled_from_contract_id');
    if (!hasSources) return;
    const handle = setTimeout(() => {
      if ((watch('rollover_sources') || []).length > 0) setValue('rollover_sources', []);
      if (watch('rolled_from_contract_id')) setValue('rolled_from_contract_id', null);
    }, 800);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMultiInvestor, rolledFundsAmount]);

  // Product-first flow: the chosen Product Type (+issuer) determines which
  // programs are available. Only MTNs are linked to programs; loans/PNs have
  // none, so the Program field is hidden for them.
  const [programsForProduct, setProgramsForProduct] = useState([]);
  const [programsLoading, setProgramsLoading] = useState(false);
  // Key (productId|issuerId) the current programsForProduct list reflects. The
  // list is LOCAL state that resets to [] when this step remounts (e.g. user
  // navigates away and back), while productId/issuerId persist in the form. The
  // program-clear effect below must NOT wipe a valid program_id off the back of
  // a stale empty list during the gap before the async refetch resolves — so it
  // only acts once this key matches the current product/issuer.
  const programsFetchedFor = React.useRef(null);

  // Fetch the programs covering the selected product type (narrowed by issuer
  // when one is set). Empty => no program for this product => hide the field.
  useEffect(() => {
    let cancelled = false;
    // The current list no longer reflects this product/issuer until the fetch
    // below resolves — mark it stale so the clear effect holds off.
    programsFetchedFor.current = null;
    if (!productId) {
      setProgramsForProduct([]);
      programsFetchedFor.current = `${productId}|${issuerId || ''}`;
      return undefined;
    }
    const fetchPrograms = async () => {
      setProgramsLoading(true);
      try {
        const params = new URLSearchParams();
        params.set('investment_type_id', productId);
        if (issuerId) params.set('issuer_id', issuerId);
        if (contractId) params.set('exclude_id', contractId);
        const response = await apiGet(`/refdata/programs?${params.toString()}`);
        const data = extractApiData(response);
        if (!cancelled) {
          setProgramsForProduct(Array.isArray(data) ? data : []);
          programsFetchedFor.current = `${productId}|${issuerId || ''}`;
        }
      } catch (err) {
        logger.error('Error fetching programs for product', { message: err.message });
        if (!cancelled) {
          setProgramsForProduct([]);
          programsFetchedFor.current = `${productId}|${issuerId || ''}`;
        }
      } finally {
        if (!cancelled) setProgramsLoading(false);
      }
    };
    fetchPrograms();
    return () => { cancelled = true; };
  }, [productId, issuerId, contractId]);

  const hasPrograms = programsForProduct.length > 0;

  // Publish the program requirement so the step validator can gate Next:
  // required once the product's program list is known to be non-empty; loading
  // while the fetch is in flight (validator blocks Next until known).
  useEffect(() => {
    setProgramRequirement({ required: hasPrograms, loading: !!productId && programsLoading });
  }, [hasPrograms, productId, programsLoading, setProgramRequirement]);

  // Capital & Investors row 1. Two layouts:
  //  - NO rolled funds: Issuer | Program | Investor share the 12-col grid evenly (capRowMd).
  //  - Rolled funds on (showRolledFrom): the identity fields pack into a LEFT md:8 zone (one
  //    line, thirds), and RolloverSourcesSection takes a RIGHT md:4 zone — the split lines up
  //    edge-for-edge with the row above (Term Unit + Term Value = 8/12, Likelihood to Roll = 4/12).
  // Issuer always shows; the others are conditional. The rolled cell is NOT part of the even
  // split, so it's excluded from capRowFieldCount.
  const showRolledFrom = !isMultiInvestor && parseFloat(rolledFundsAmount) > 0;
  const capRowFieldCount =
    1 + (hasPrograms ? 1 : 0) + (!isMultiInvestor ? 1 : 0);
  const capRowMd = Math.max(3, Math.floor(12 / capRowFieldCount));
  // Inside the LEFT md:8 zone the identity fields share the 12-col nested grid: thirds when
  // Program is present (Group | Program | Investor), halves when it isn't (Group | Investor).
  const idZoneMd = hasPrograms ? 4 : 6;

  // Clear a selected program that's no longer valid for the current product/
  // issuer (e.g. product changed to one the program doesn't cover, or the list
  // became empty). Keeps program_id consistent with the DB hard-block.
  useEffect(() => {
    // Only act on a list that definitively reflects the CURRENT product/issuer.
    // On remount the list is briefly an empty placeholder before the refetch
    // resolves; clearing program_id then would silently wipe a valid selection
    // (and cascade to the issuer/investor group it drives). The ref guard makes
    // this fire only once the fetch for this exact product/issuer has completed.
    if (programsLoading) return;
    if (programsFetchedFor.current !== `${productId}|${issuerId || ''}`) return;
    const current = watch('program_id');
    if (current && !programsForProduct.some((p) => p.id === current)) {
      setValue('program_id', '');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programsForProduct, programsLoading, productId, issuerId]);

  // Two-way coherence: if the user manually picks a DIFFERENT issuer while a
  // program is selected, clear the program immediately (don't wait for the
  // programs-list refetch to drop it). The reverse direction — program drives
  // issuer — is handled inline in the Program field's onChange.
  useEffect(() => {
    const currentProgramId = watch('program_id');
    if (!currentProgramId || !issuerId) return;
    const selected = programsForProduct.find((p) => p.id === currentProgramId);
    if (selected && selected.issuer_id !== issuerId) {
      setValue('program_id', '');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issuerId]);

  // Which side of the term/maturity pair the user last edited.
  // 'term' | 'maturity' drive the corresponding effect; 'loaded' keeps both idle on edit-mode mount.
  const [lastEditedSide, setLastEditedSide] = useState(isEdit ? 'loaded' : null);

  // Track whether the funds received date has been explicitly set (either
  // persisted from a prior save, or just typed by the user in this session).
  //
  // CRITICAL: This MUST be initialised synchronously from initialData so the
  // auto-sync effect below sees the correct flag on the very FIRST render.
  // Earlier versions used `useState(false)` + a useEffect to set the flag
  // post-mount, which created a React effect-ordering race: the auto-sync
  // effect (declared later) reads the render snapshot, not the post-effect
  // state, so it would clobber the just-loaded funds_received_date with
  // deposit_date on initial mount. The clobbered value would then be saved
  // on the next Save & Close, silently corrupting the user's prior edit.
  //
  // The field's onChange handler keeps flipping the flag true during a
  // session (see the Controller below). The flag only ever needs to flip
  // false in create-mode, which the initializer already handles.
  const [fundsReceivedOverridden, setFundsReceivedOverridden] = useState(
    () => isEdit && !!initialData?.funds_received_date
  );

  // Effect A: term -> maturity
  useEffect(() => {
    if (lastEditedSide !== 'term') return;
    if (!depositDate || !investmentTermValue) return;
    const m = calculateMaturityDate(depositDate, investmentTerm, parseInt(investmentTermValue));
    if (m && !isNaN(m.getTime())) {
      setValue('maturity_date', format(m, 'yyyy-MM-dd'));
    }
  }, [depositDate, investmentTerm, investmentTermValue, lastEditedSide, setValue]);

  // Effect B: maturity -> term
  useEffect(() => {
    if (lastEditedSide !== 'maturity') return;
    if (!depositDate || !maturityDate) return;
    const v = diffInUnit(depositDate, maturityDate, investmentTerm);
    setValue('investment_term_value', v != null && v > 0 ? v : '');
  }, [depositDate, maturityDate, investmentTerm, lastEditedSide, setValue]);

  // Auto-sync funds_received_date to deposit_date (unless manually overridden)
  useEffect(() => {
    if (!fundsReceivedOverridden && depositDate) {
      setValue('funds_received_date', depositDate);
    }
  }, [depositDate, fundsReceivedOverridden, setValue]);

  // Auto-compute capital_amount from new_funds + rolled_funds (single-investor mode)
  useEffect(() => {
    if (!isMultiInvestor) {
      const newFunds = parseFloat(newFundsAmount) || 0;
      const rolledFunds = parseFloat(rolledFundsAmount) || 0;
      const total = newFunds + rolledFunds;
      if (total > 0) {
        setValue('capital_amount', total);
      }
    }
  }, [isMultiInvestor, newFundsAmount, rolledFundsAmount, setValue]);

  // Auto-sync capital amount and fund sources from multi-investor totals
  useEffect(() => {
    if (isMultiInvestor && contractInvestors.length > 0) {
      const totalCapital = contractInvestors.reduce(
        (sum, inv) => sum + parseFloat(inv.capital_amount || 0),
        0
      );
      const totalNew = contractInvestors.reduce(
        (sum, inv) => sum + parseFloat(inv.new_funds_amount || 0),
        0
      );
      const totalRolled = contractInvestors.reduce(
        (sum, inv) => sum + parseFloat(inv.rolled_funds_amount || 0),
        0
      );
      setValue('capital_amount', totalCapital);
      setValue('new_funds_amount', totalNew);
      setValue('rolled_funds_amount', totalRolled);
    }
  }, [isMultiInvestor, contractInvestors, setValue]);


  return (
    <Box component="fieldset" disabled={!isEditable} sx={{ border: 'none', p: 0, m: 0 }}>
      <Card>
        <CardContent>
          {/* ===== Section 1: Contract Details ===== */}
          <Typography variant="h6" gutterBottom>
            Contract Details
          </Typography>

          <Grid container spacing={3}>
            <Grid
              size={{
                xs: 12,
                md: 3
              }}>
              <Controller
                name="user_reference"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="External Reference"
                    fullWidth
                    placeholder="e.g., Client Deal #123"
                    inputProps={{ maxLength: 100 }}
                    InputLabelProps={{ shrink: true }}
                    // T127: soft duplicate warning — informational only, does not block.
                    color={refDuplicate ? 'warning' : undefined}
                    focused={refDuplicate ? true : undefined}
                    helperText={
                      refDuplicate
                        ? `Already used on ${refDuplicate.count} other contract${refDuplicate.count === 1 ? '' : 's'}${refDuplicate.samples?.[0]?.reference ? ` (e.g. ${refDuplicate.samples[0].reference})` : ''} — you can still proceed.`
                        : 'Optional external reference'
                    }
                    FormHelperTextProps={refDuplicate ? { sx: { color: 'warning.main' } } : undefined}
                  />
                )}
              />
            </Grid>

            {/* Product Type — chosen first; drives the Program field in Capital & Investors */}
            <Grid
              size={{
                xs: 12,
                md: 3
              }}>
              <Controller
                name="product_id"
                control={control}
                rules={{ required: 'Product type is required' }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    select
                    label="Product Type *"
                    fullWidth
                    error={!!error}
                    helperText={error?.message}
                  >
                    <MenuItem value="">
                      <em>Select product...</em>
                    </MenuItem>
                    {(refData.products || []).map((product) => (
                      <MenuItem key={product.id} value={product.id}>
                        {product.type}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 3
              }}>
              <Controller
                name="investment_currency_id"
                control={control}
                rules={{ required: 'Contract currency is required' }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    select
                    label="Contract Currency *"
                    fullWidth
                    error={!!error}
                    helperText={error?.message || 'Currency the contract accrues and runs in'}
                  >
                    <MenuItem value="">
                      <em>Select...</em>
                    </MenuItem>
                    {refData.currencies
                      .filter((c) => c.currency_code && c.active !== false)
                      .map((currency) => (
                        <MenuItem key={currency.id} value={currency.id}>
                          {currency.currency_code}
                        </MenuItem>
                      ))}
                  </TextField>
                )}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 3
              }}>
              <TextField
                label="Country"
                value={selectedCurrency?.country || ''}
                fullWidth
                disabled
                InputLabelProps={{ shrink: true }}
                placeholder="Select currency first"
                helperText="Based on selected currency"
              />
            </Grid>

            {/* T117: Settlement currency. The contract still accrues/runs in the currency
                above; the picker lines up under the Contract Currency column (3rd of 4). */}
            <Grid container item size={{ xs: 12 }} spacing={3} alignItems="flex-start">
              <Grid size={{ xs: 12, md: 3 }}>
                <InlineGateToggle
                  label="Settle in a different currency"
                  caption={`The contract accrues and runs in ${currencyCode}. Capital receipts, capital repayments and interest payments will be settled in the chosen currency.`}
                  checked={settleDifferent}
                  onChange={(e) => {
                    const on = e.target.checked;
                    setSettleDifferent(on);
                    // Turning it off clears the fields so the contract is single-currency.
                    if (!on) {
                      setValue('settlement_currency_id', '');
                      setValue('settlement_fx_rate', '');
                      setValue('capital_received_in_settlement_ccy', false);
                    }
                  }}
                />
              </Grid>
              {/* Always render the picker column so enabling the toggle does not change the
                  row height (which would push the section divider down). Hidden when off. */}
              <Grid size={{ xs: 12, md: 3 }} sx={{ visibility: settleDifferent ? 'visible' : 'hidden' }}>
                <Controller
                  name="settlement_currency_id"
                  control={control}
                  rules={{
                    validate: (value) => {
                      if (!settleDifferent) return true;
                      if (!value) return 'Settlement currency is required';
                      if (value === investmentCurrencyId) {
                        return 'Settlement currency must differ from the contract currency';
                      }
                      return true;
                    },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      select
                      label="Settlement Currency *"
                      fullWidth
                      error={!!error}
                      helperText={error?.message || 'Currency the investor pays/receives in'}
                    >
                      <MenuItem value="">
                        <em>Select...</em>
                      </MenuItem>
                      {refData.currencies
                        .filter((c) => c.currency_code && c.active !== false && c.id !== investmentCurrencyId)
                        .map((currency) => (
                          <MenuItem key={currency.id} value={currency.id}>
                            {currency.currency_code}
                          </MenuItem>
                        ))}
                    </TextField>
                  )}
                />
              </Grid>
              {/* T127: receipt direction. Records whether the INITIAL capital funds physically
                  arrive in the settlement currency or directly in the contract currency. The
                  contract amount is always entered in the contract currency; this only drives
                  how the capital receipt / schedule shows the foreign-currency figure. Sits
                  between the settlement currency and the rate; same hidden-when-off discipline
                  as the others so all four fields share one row. */}
              <Grid size={{ xs: 12, md: 3 }} sx={{ visibility: settleDifferent ? 'visible' : 'hidden' }}>
                <Controller
                  name="capital_received_in_settlement_ccy"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      select
                      label="Initial funds received in"
                      fullWidth
                      value={field.value ? 'settlement' : 'contract'}
                      onChange={(e) => field.onChange(e.target.value === 'settlement')}
                      helperText={
                        field.value
                          ? `Funds arrive in ${settlementCurrencyCode || 'the settlement currency'}; the capital receipt converts to ${currencyCode} at the initial capital rate.`
                          : `Funds arrive directly in ${currencyCode} (the contract currency).`
                      }
                    >
                      <MenuItem value="contract">
                        Contract currency{currencyCode ? ` (${currencyCode})` : ''}
                      </MenuItem>
                      <MenuItem value="settlement">
                        Settlement currency{settlementCurrencyCode ? ` (${settlementCurrencyCode})` : ''}
                      </MenuItem>
                    </TextField>
                  )}
                />
              </Grid>
              {/* T119: initial-capital FX rate — agreed rate for the initial capital drawdown.
                  Recommended from the system rate, overridable. Interest & later settlements
                  use the live rate, not this. Same hidden-when-off discipline. */}
              <Grid size={{ xs: 12, md: 3 }} sx={{ visibility: settleDifferent ? 'visible' : 'hidden' }}>
                <Controller
                  name="settlement_fx_rate"
                  control={control}
                  rules={{
                    validate: (value) => {
                      if (!settleDifferent) return true;
                      if (value === undefined || value === null || value === '') return 'Initial capital rate is required';
                      if (!(parseFloat(value) > 0)) return 'Rate must be greater than 0';
                      return true;
                    },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label={`Initial Capital Rate${settlementCurrencyCode && currencyCode ? ` (${settlementCurrencyCode}/${currencyCode})` : ''} *`}
                      fullWidth
                      type="number"
                      inputProps={{ inputMode: 'decimal', step: 'any' }}
                      error={!!error}
                      helperText={
                        error?.message
                          || (fxRecommending ? 'Loading recommended rate…'
                              : 'Agreed rate for the initial capital; override as needed. Interest & later settlements use the live rate.')
                      }
                    />
                  )}
                />
              </Grid>
            </Grid>

            {/* Multi-investor Toggle - editable on create AND on editable (Draft)
                contracts, so a Draft can be reverted to single-investor. */}
            {isEditable && (
              <Grid size={12}>
                <ToggleGroup>
                  <Controller
                    name="is_multi_investor"
                    control={control}
                    render={({ field }) => (
                      <LabeledSwitch
                        label="Multiple Investors"
                        caption="Enable this if multiple investors will participate in this contract"
                        checked={field.value || false}
                        onChange={(e) => handleMultiInvestorToggle(e.target.checked, field.onChange)}
                      />
                    )}
                  />
                </ToggleGroup>
              </Grid>
            )}

            {/* Read-only badge for non-editable (Approved/Active/Matured)
                multi-investor contracts — the flag can no longer be changed. */}
            {isEdit && !isEditable && watch('is_multi_investor') && (
              <Grid size={12}>
                <Chip label="Multi-Investor Contract" color="primary" variant="outlined" />
              </Grid>
            )}
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* ===== Section 2: Term & Dates ===== */}
          <Typography variant="h6" gutterBottom>
            Term & Dates
          </Typography>

          <Grid container spacing={3}>
            {/* Row 1: Start | Maturity | Funds Received | First Payment */}
            <Grid
              size={{
                xs: 12,
                md: 3
              }}>
              <Controller
                name="deposit_date"
                control={control}
                rules={{ required: 'Start date is required' }}
                render={({ field, fieldState: { error } }) => (
                  <ClearableDateField
                    field={field}
                    label="Start Date *"
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 3
              }}>
              <Controller
                name="maturity_date"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <ClearableDateField
                    field={field}
                    onChange={(e) => {
                      field.onChange(e);
                      // Only claim "manually set" for a real value. Clearing
                      // (empty) hands control back to the term -> maturity auto-calc.
                      setLastEditedSide(e.target.value ? 'maturity' : 'term');
                    }}
                    onClear={() => setLastEditedSide('term')}
                    label="Maturity Date"
                    error={!!error}
                    helperText={lastEditedSide === 'maturity' ? 'Manually set' : 'Auto-calculated'}
                  />
                )}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 3
              }}>
              <Controller
                name="funds_received_date"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <ClearableDateField
                    field={field}
                    onChange={(e) => {
                      field.onChange(e);
                      // A real value is a manual override; clearing hands control
                      // back to the auto-sync effect (re-defaults to start date).
                      setFundsReceivedOverridden(!!e.target.value);
                    }}
                    onClear={() => setFundsReceivedOverridden(false)}
                    label="Funds Received Date"
                    error={!!error}
                    helperText={fundsReceivedOverridden ? 'Manually set' : 'Defaults to start date'}
                  />
                )}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 3
              }}>
              <Controller
                name="first_payment_date"
                control={control}
                rules={{
                  validate: (value) => {
                    if (!value) return true;
                    if (depositDate && new Date(value) < new Date(depositDate)) return 'Must be on or after start date';
                    if (maturityDate && new Date(value) > new Date(maturityDate)) return 'Must be on or before maturity date';
                    return true;
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <ClearableDateField
                    field={field}
                    label="First Payment Date"
                    inputProps={{ min: depositDate || undefined, max: maturityDate || undefined }}
                    error={!!error}
                    helperText={error?.message || (field.value
                      ? 'Anchors interest and capital payment cycles'
                      : 'Defaults to start date')}
                  />
                )}
              />
            </Grid>

            {/* Row 2: Term Unit | Term Value | Likelihood to Roll */}
            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="investment_term"
                control={control}
                render={({ field }) => (
                  <TextField {...field} select label="Term Unit" fullWidth>
                    <MenuItem value="Days">Days</MenuItem>
                    <MenuItem value="Months">Months</MenuItem>
                    <MenuItem value="Years">Years</MenuItem>
                  </TextField>
                )}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="investment_term_value"
                control={control}
                rules={{
                  required: 'Investment term is required',
                  validate: (value) => {
                    const num = parseFloat(value);
                    if (isNaN(num)) return 'Must be a valid number';
                    if (num <= 0) return 'Must be greater than zero';
                    return true;
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      setLastEditedSide('term');
                    }}
                    label="Term Value *"
                    type="number"
                    fullWidth
                    inputProps={{ step: 0.01, min: 0.01 }}
                    InputLabelProps={{ shrink: true }}
                    placeholder="e.g., 12"
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid>

            {/* Contract-level LTR — hidden for multi-investor (each investor has their own) */}
            {!isMultiInvestor && (
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <Controller
                  name="likelihood_to_roll"
                  control={control}
                  render={({ field, fieldState: { error } }) => {
                    const missingRequired = requireLikelihoodToRoll && !field.value;
                    return (
                    <TextField
                      {...field}
                      select
                      label={requireLikelihoodToRoll ? 'Likelihood to Roll *' : 'Likelihood to Roll'}
                      fullWidth
                      error={!!error || missingRequired}
                      helperText={
                        error?.message
                        || (missingRequired ? 'Likelihood to Roll is required' : 'Expected behavior at maturity')
                      }
                    >
                      <MenuItem value="">
                        <em>Not specified</em>
                      </MenuItem>
                      <MenuItem value="Likely">Likely</MenuItem>
                      <MenuItem value="Middle">Possible</MenuItem>
                      <MenuItem value="Not likely">Not likely</MenuItem>
                    </TextField>
                    );
                  }}
                />
              </Grid>
            )}
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* ===== Section 3: Capital & Investors ===== */}
          <Typography variant="h6" gutterBottom>
            Capital & Investors
          </Typography>

          <Grid container spacing={3}>
            {/* Row 1: identity fields (Issuer/Group | Program | Investor) + rolled-from sources.
                Two layouts (see capRowMd / idZoneMd above):
                  - no rolled funds: the identity fields share the 12-col grid evenly (capRowMd);
                  - rolled funds on: identity fields pack into a LEFT md:8 zone (thirds) and the
                    RolloverSourcesSection takes a RIGHT md:4 zone, aligned edge-for-edge with the
                    row above (Term Unit + Term Value = 8/12, Likelihood to Roll = 4/12).
                The three <Controller> fields render once; only their wrapping Grid changes.
                idFields is the shared field markup; idFieldMd is capRowMd (flat) or idZoneMd (in
                the left zone). */}
            {(() => {
              const idFieldMd = showRolledFrom ? idZoneMd : capRowMd;
              const issuerField = (
                <Grid key="issuer" size={{ xs: 12, md: idFieldMd }}>
                  <Controller
                    name="issuer_id"
                    control={control}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <StakeholderAutocomplete
                        stakeholderType="issuer"
                        options={refData.issuers || []}
                        value={value || ''}
                        onChange={(id) => onChange(id || null)}
                        onStakeholderCreated={(newIssuer) => {
                          appendToRefData('issuers', newIssuer);
                          onChange(newIssuer.id);
                        }}
                        label={issuerLabel}
                        error={!!error}
                        helperText={error?.message}
                        countries={refData.currencies}
                        disabled={!isEditable}
                      />
                    )}
                  />
                </Grid>
              );
              // Program (capped) — shown only when the chosen Product Type has linked programs;
              // required when present. Hidden for loans/PNs.
              const programField = hasPrograms ? (
                <Grid key="program" size={{ xs: 12, md: idFieldMd }}>
                  <Controller
                    name="program_id"
                    control={control}
                    rules={{ validate: (v) => (!hasPrograms || !!v) || 'Program is required for this product' }}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <ProgramAutocomplete
                        value={value || null}
                        onChange={(id, programObj) => {
                          onChange(id || null);
                          // Program drives issuer when set: back-fill from
                          // programObj.issuer_id (always wins, even when switching
                          // between programs of different issuers). When the user
                          // CLEARS the program, also clear the issuer — a wrong
                          // program likely means the issuer was wrong too.
                          if (programObj && programObj.issuer_id) {
                            setValue('issuer_id', programObj.issuer_id);
                          } else if (!id) {
                            setValue('issuer_id', '');
                          }
                        }}
                        options={programsForProduct}
                        issuerId={issuerId}
                        investmentTypeId={productId}
                        excludeContractId={contractId}
                        capitalAmount={capitalAmount}
                        contractCurrencyCode={selectedCurrency?.currency_code || null}
                        label={`${issuerLabel} Program *`}
                        error={error}
                        disabled={!isEditable || programsLoading}
                      />
                    )}
                  />
                </Grid>
              ) : null;
              const investorField = !isMultiInvestor ? (
                <Grid key="investor" size={{ xs: 12, md: idFieldMd }}>
                  <Controller
                    name="investor_id"
                    control={control}
                    rules={{ required: !isMultiInvestor ? 'Investor is required' : false }}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <StakeholderAutocomplete
                        stakeholderType="investor"
                        options={refData.investors}
                        value={value}
                        onChange={(id) => onChange(id)}
                        onStakeholderCreated={(newInvestor) => {
                          appendToRefData('investors', newInvestor);
                          onChange(newInvestor.id);
                        }}
                        label="Investor / Counterparty *"
                        error={!!error}
                        helperText={error?.message}
                        countries={refData.currencies}
                        disabled={!isEditable}
                      />
                    )}
                  />
                </Grid>
              ) : null;

              const idFields = [issuerField, programField, investorField].filter(Boolean);

              // T3-032: many-to-1 rolled funds — a single-investor contract can roll from one OR
              // MORE prior contracts (same investor). Rows persist in the `rollover_sources` form
              // field; the section validates per-source cap + that rows sum to Rolled Funds, and
              // publishes a blocking message to `rolledFundsError` so Next is gated.
              const sourcesBlock = (
                <RolloverSourcesSection
                  sources={rolloverSources}
                  onRowsChange={(rowsNext) => setValue('rollover_sources', rowsNext, { shouldDirty: true })}
                  onChange={(payloadSources, err) => {
                    // Keep the contract-level mirror + validation state in sync.
                    setValue('rolled_from_contract_id', payloadSources[0]?.source_contract_id || null);
                    setRolledFundsError(err || null);
                  }}
                  rolledFundsTotal={rolledFundsAmount}
                  excludeId={contractId}
                  disabled={!isEditable}
                  currencyCode={currencyCode}
                  depositDate={depositDate}
                  investorId={watch('investor_id')}
                  investorName={(refData.investors || []).find(i => i.id === watch('investor_id'))?.name}
                />
              );

              if (showRolledFrom) {
                // Split: identity fields in a LEFT md:8 nested grid (thirds/halves), sources in
                // a RIGHT md:4 zone — aligned under the row above.
                return (
                  <>
                    <Grid size={{ xs: 12, md: 8 }}>
                      <Grid container spacing={3}>
                        {idFields}
                      </Grid>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      {sourcesBlock}
                    </Grid>
                  </>
                );
              }
              // No rolled funds: identity fields share the full 12-col grid evenly.
              return idFields;
            })()}

            {/* Multi-investor section */}
            {isMultiInvestor && (
              <Grid size={12}>
                <ContractInvestorsSection
                  contractId={contractId}
                  investors={contractInvestors}
                  availableInvestors={refData.investors}
                  onAddInvestor={handleAddInvestor}
                  onUpdateInvestor={handleUpdateInvestor}
                  onRemoveInvestor={handleRemoveInvestor}
                  onInvestorCreated={(newInvestor) => appendToRefData('investors', newInvestor)}
                  onPriorContractSelected={(id) => setValue('rolled_from_contract_id', id)}
                  currencyCode={currencyCode}
                  countries={refData.currencies}
                  depositDate={depositDate}
                  disabled={!isEditable}
                  rolledFromContractId={rolledFromContractId}
                  rolledFromReference={watch('rolled_from_reference')}
                  rolledFromUserReference={watch('rolled_from_user_reference')}
                  requireLikelihood={requireLikelihoodToRoll}
                />
              </Grid>
            )}

            {/* Force amounts onto their own row */}
            <Grid size={12} sx={{ p: 0, height: 0 }} />

            {/* Row 2: New Funds | Rolled Funds | Total Capital */}
            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="new_funds_amount"
                control={control}
                rules={{
                  validate: (value) => {
                    if (isMultiInvestor) return true;
                    const num = parseFloat(value);
                    if (isNaN(num) && !value) return true;
                    if (num < 0) return 'Must be zero or positive';
                    return true;
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <CurrencyInput
                    value={field.value}
                    onChange={field.onChange}
                    label={`New Funds${captureCcy ? ` (${captureCcy})` : ''}`}
                    helperText={capitalInSettlementCcy
                      ? `Fresh capital received in ${captureCcy} (converted to ${currencyCode})`
                      : 'Fresh capital from new sources'}
                    error={error}
                    disabled={isMultiInvestor}
                  />
                )}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="rolled_funds_amount"
                control={control}
                rules={{
                  validate: (value) => {
                    if (isMultiInvestor) return true;
                    const num = parseFloat(value);
                    if (isNaN(num) && !value) return true;
                    if (num < 0) return 'Must be zero or positive';
                    return true;
                  },
                }}
                render={({ field, fieldState: { error } }) => {
                  // Highlight this field too when the sources don't reconcile to it —
                  // the mismatch is as much about THIS total as the source rows, so both
                  // should flag red (single-investor rolled path only). To avoid printing
                  // the SAME sentence twice, this field shows a SHORT pointer and the
                  // sources section carries the full detail (the two blocks share the
                  // message). error is the RHF field error; rolledFundsError is the
                  // section-level blocker.
                  const showRolledMismatch = !isMultiInvestor && !!rolledFundsError;
                  return (
                    <CurrencyInput
                      value={field.value}
                      onChange={field.onChange}
                      label={`Rolled Funds${captureCcy ? ` (${captureCcy})` : ''}`}
                      helperText={error?.message
                        || (showRolledMismatch ? "Doesn't match the rolled-fund sources — see the sources total" : null)
                        || (capitalInSettlementCcy
                          ? `Capital rolled in, received in ${captureCcy} (converted to ${currencyCode})`
                          : 'Total rolled from prior contract(s) — allocate below')}
                      error={!!error || showRolledMismatch}
                      disabled={isMultiInvestor}
                    />
                  );
                }}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="capital_amount"
                control={control}
                rules={{
                  required: 'Capital amount is required',
                  validate: (value) => {
                    const num = parseFloat(value);
                    if (isNaN(num)) return 'Must be a valid number';
                    if (num <= 0) return 'Must be greater than zero';
                    return true;
                  },
                }}
                render={({ field, fieldState: { error } }) => {
                  // The form holds capital_amount in the CAPTURE currency (New+Rolled as typed).
                  // When funds are received in the settlement ccy, show the CONTRACT-currency
                  // figure the contract will actually run on (= entered × rate), so the user sees
                  // the converted amount before saving. buildPayload performs the real conversion.
                  const enteredTotal = parseFloat(field.value);
                  const rate = parseFloat(watch('settlement_fx_rate'));
                  const showConverted = capitalInSettlementCcy && Number.isFinite(enteredTotal) && Number.isFinite(rate) && rate > 0;
                  const convertedTotal = showConverted ? (enteredTotal * rate) : null;
                  return (
                    <CurrencyInput
                      value={showConverted ? convertedTotal : field.value}
                      onChange={field.onChange}
                      label={`Total Capital *${capitalInSettlementCcy ? ` (${currencyCode})` : ''}`}
                      helperText={showConverted
                        ? `${formatWithSpaces(enteredTotal)} ${captureCcy} × ${rate} = ${formatWithSpaces(convertedTotal)} ${currencyCode} (contract runs on this)`
                        : 'New Funds + Rolled Funds'}
                      error={error}
                      disabled
                      sx={{ '& .MuiInputBase-input.Mui-disabled': { fontWeight: 'bold', WebkitTextFillColor: 'rgba(0,0,0,0.87)' } }}
                    />
                  );
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Confirm before turning OFF multi-investor on a Draft that has
          allocations — this removes every investor allocation (irreversible). */}
      <Dialog
        open={pendingMultiOff !== null}
        onClose={() => setPendingMultiOff(null)}
      >
        <DialogTitle>Switch to single investor?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This contract has <strong>{(contractInvestors || []).length}</strong> investor
            allocation(s). Turning off Multiple Investors will remove them. This cannot be
            undone — you will then need to select a single investor for the contract.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingMultiOff(null)}>Cancel</Button>
          <Button onClick={confirmMultiOff} color="error" variant="contained">
            Remove allocations
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ContractDetailsStep;

